import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { BarChart3, TrendingUp, TrendingDown, Eye, Crown, ArrowUpRight, Minus } from 'lucide-react'
import { getCreatorOverview, type CreatorOverview } from '../lib/api'
import IncludeOwnerToggle from '../components/analytics/IncludeOwnerToggle'

export default function CreatorDashboard() {
  const [overview, setOverview] = useState<CreatorOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [includeOwner, setIncludeOwner] = useState(false)
  // "Only published" filters the dashboard to metadata.slot='prod'
  // visits — dev / branch traffic from the creator iterating on their
  // app is excluded. Default OFF (all slots count) so totals match
  // /admin/activity's default behavior; opt in to see real traction.
  const [onlyPublished, setOnlyPublished] = useState(false)

  useEffect(() => {
    getCreatorOverview()
      .then(setOverview)
      .catch((err) => setError(err.message || 'Failed to load analytics'))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            My Analytics
          </h1>
          <p className="text-muted-foreground mt-1">See how your apps are performing</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-36 animate-pulse bg-secondary rounded-xl" />
          ))}
        </div>
        <div className="h-64 animate-pulse bg-secondary rounded-xl mt-6" />
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            My Analytics
          </h1>
          <p className="text-muted-foreground mt-1">See how your apps are performing</p>
        </div>
        <div className="p-6 rounded-xl border border-destructive/30 bg-destructive/5 text-center">
          <p className="text-destructive mb-4">{error}</p>
          <button
            onClick={() => {
              setLoading(true)
              setError(null)
              getCreatorOverview()
                .then(setOverview)
                .catch((err) => setError(err.message || 'Failed to load analytics'))
                .finally(() => setLoading(false))
            }}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!overview || overview.total_apps === 0) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            My Analytics
          </h1>
          <p className="text-muted-foreground mt-1">See how your apps are performing</p>
        </div>
        <div className="p-12 rounded-xl border border-border bg-card text-center">
          <Eye className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-lg font-semibold mb-2">No apps yet</h2>
          <p className="text-muted-foreground mb-6">Create your first app to see analytics here</p>
          <Link
            to="/apps"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <ArrowUpRight className="w-4 h-4" />
            Go to My Apps
          </Link>
        </div>
      </div>
    )
  }

  const wowSign = overview.wow_change > 0 ? '+' : ''
  const wowColor = overview.wow_change > 0
    ? 'text-green-500'
    : overview.wow_change < 0
      ? 'text-red-500'
      : 'text-muted-foreground'
  const WowIcon = overview.wow_change > 0
    ? TrendingUp
    : overview.wow_change < 0
      ? TrendingDown
      : Minus

  // Per-app field picks based on the two toggles:
  //   onlyPublished=false → ALL slots (visit_count_30d et al)
  //   onlyPublished=true  → prod only (visit_count_prod_30d et al)
  //   includeOwner adds the owner's own visits on top of either set.
  const pickExternal = (app: typeof overview.app_stats[0]) =>
    onlyPublished ? app.visit_count_prod_30d : app.visit_count_30d
  const pickExternalUnique = (app: typeof overview.app_stats[0]) =>
    onlyPublished ? app.unique_visitors_prod_30d : app.unique_visitors_30d
  const pickOwner = (app: typeof overview.app_stats[0]) =>
    onlyPublished ? app.owner_visit_count_prod_30d : app.owner_visit_count_30d
  const pickOwnerUnique = (app: typeof overview.app_stats[0]) =>
    onlyPublished ? app.owner_unique_visitors_prod_30d : app.owner_unique_visitors_30d

  // Sum from app_stats. total_visitors_30d on the API is all-slot
  // external — for "Only published" we recompute from per-app prod
  // fields. Unique-visitor totals are approximate when summed across
  // apps (existing behavior — visitors may overlap across apps).
  const totalExternal = onlyPublished
    ? overview.app_stats.reduce((s, a) => s + a.visit_count_prod_30d, 0)
    : overview.total_visitors_30d
  const totalExternalUnique = onlyPublished
    ? overview.app_stats.reduce((s, a) => s + a.unique_visitors_prod_30d, 0)
    : overview.total_unique_visitors_30d

  const ownerVisits = overview.app_stats.reduce((sum, a) => sum + pickOwner(a), 0)
  const ownerUnique = overview.app_stats.reduce((sum, a) => sum + pickOwnerUnique(a), 0)
  const displayedTotal = totalExternal + (includeOwner ? ownerVisits : 0)
  const displayedUnique = totalExternalUnique + (includeOwner ? ownerUnique : 0)

  const sortedApps = [...overview.app_stats].sort((a, b) => {
    const av = pickExternal(a) + (includeOwner ? pickOwner(a) : 0)
    const bv = pickExternal(b) + (includeOwner ? pickOwner(b) : 0)
    return bv - av
  })

  const mostVisitedApp = sortedApps[0]
  const mostVisitedCount = mostVisitedApp
    ? pickExternal(mostVisitedApp) + (includeOwner ? pickOwner(mostVisitedApp) : 0)
    : 0

  // The owner toggle stays hidden until there's owner data to fold in
  // (less clutter); the published toggle is always visible because
  // its effect is meaningful even without owner visits.
  const showOwnerToggle =
    overview.app_stats.reduce((s, a) => s + a.owner_visit_count_30d, 0) > 0 || includeOwner

  return (
    <div>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            My Analytics
          </h1>
          <p className="text-muted-foreground mt-1">See how your apps are performing</p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <IncludeOwnerToggle
            checked={onlyPublished}
            onCheckedChange={setOnlyPublished}
            label="Only published (prod)"
          />
          {showOwnerToggle && (
            <IncludeOwnerToggle checked={includeOwner} onCheckedChange={setIncludeOwner} />
          )}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {/* Total Visitors */}
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 text-muted-foreground mb-3">
            <Eye className="w-5 h-5" />
            <span className="text-sm font-medium">Total Visitors</span>
          </div>
          <p className="text-2xl font-bold">{displayedTotal.toLocaleString()}</p>
          <p className="text-sm text-muted-foreground mt-1">
            visitors in 30 days
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {displayedUnique.toLocaleString()} unique
          </p>
        </div>

        {/* Most Visited App */}
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 text-muted-foreground mb-3">
            <Crown className="w-5 h-5" />
            <span className="text-sm font-medium">Most Visited App</span>
          </div>
          {mostVisitedApp && mostVisitedCount > 0 ? (
            <>
              <Link
                to={`/apps/${mostVisitedApp.app_name}`}
                className="text-xl font-bold text-primary hover:underline"
              >
                {mostVisitedApp.app_name}
              </Link>
              <p className="text-sm text-muted-foreground mt-1">
                {mostVisitedCount.toLocaleString()} visits
              </p>
            </>
          ) : (
            <p className="text-xl font-bold text-muted-foreground">None yet</p>
          )}
        </div>

        {/* Week over Week — always external-only. Owner traffic is not a
            traction signal so we keep it out of the comparison even when
            the toggle is on. */}
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 text-muted-foreground mb-3">
            <WowIcon className="w-5 h-5" />
            <span className="text-sm font-medium">Week over Week</span>
          </div>
          <p className={`text-2xl font-bold ${wowColor}`}>
            {wowSign}{overview.wow_change.toFixed(1)}%
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {overview.this_week_visits} this week vs {overview.last_week_visits} last week
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">external visitors only</p>
        </div>
      </div>

      {/* Per-App Breakdown */}
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Per-App Breakdown</h2>
        {sortedApps.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-sm font-medium text-muted-foreground py-3 pr-4">App Name</th>
                  <th className="text-left text-sm font-medium text-muted-foreground py-3 pr-4">Display Name</th>
                  <th className="text-right text-sm font-medium text-muted-foreground py-3 pr-4">Visitors (30d)</th>
                  <th className="text-right text-sm font-medium text-muted-foreground py-3">Unique Visitors (30d)</th>
                </tr>
              </thead>
              <tbody>
                {sortedApps.map((app) => {
                  const visits = pickExternal(app) + (includeOwner ? pickOwner(app) : 0)
                  const unique =
                    pickExternalUnique(app) + (includeOwner ? pickOwnerUnique(app) : 0)
                  return (
                    <tr key={app.app_name} className="border-b border-border last:border-0">
                      <td className="py-3 pr-4">
                        <Link
                          to={`/apps/${app.app_name}`}
                          className="text-sm font-medium text-primary hover:underline"
                        >
                          {app.app_name}
                        </Link>
                      </td>
                      <td className="py-3 pr-4 text-sm text-muted-foreground">
                        {app.display_name || '-'}
                      </td>
                      <td className="py-3 pr-4 text-right text-sm font-medium">
                        {visits.toLocaleString()}
                      </td>
                      <td className="py-3 text-right text-sm font-medium">
                        {unique.toLocaleString()}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">No visitor data yet</p>
        )}
      </div>
    </div>
  )
}
